import { Stack } from '@mui/material';
import { useGetAreasOfLearningQuery } from '@schooly/api';
import { FC, useCallback, useRef, useState } from 'react';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { DropdownSelectRow } from '../DropdownSelectRow';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';

type AreaOfLearningSuggestedInputProps = {
  schoolId: string;
  value: string;
  onChange: (v: string) => void;
} & Omit<DropdownSelectProps, 'children' | 'renderContent'>;

export const AreaOfLearningSuggestedInput: FC<AreaOfLearningSuggestedInputProps> = ({
  schoolId,
  value,
  onChange,
  placeholder,
  ...dropdownProps
}) => {
  const dropdown = useRef<DropdownSelect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [queryEnabled, setQueryEnabled] = useState(false);

  const { data } = useGetAreasOfLearningQuery(schoolId, {
    enabled: queryEnabled && !!schoolId,
    refetchOnMount: 'always',
  });

  const renderContent = useCallback(() => {
    if (!data) return <SelectContentSkeleton />;

    const entries = data.filter((a) => {
      const name = a.name.toLowerCase();
      const v = value.toLowerCase();

      return name.includes(v.trim());
    });

    if (!entries.length) return null;

    return (
      <Stack gap={0.5} m={1}>
        {entries.map((area) => {
          const isSelected = area.name.trim() === value.trim();

          return (
            <DropdownSelectRow
              key={area.id}
              isSelected={isSelected}
              onClick={() => {
                onChange(area.name);
                dropdown.current?.close();
              }}
            >
              {area.name}
            </DropdownSelectRow>
          );
        })}
      </Stack>
    );
  }, [data, onChange, value]);

  return (
    <DropdownSelect
      {...dropdownProps}
      ref={dropdown}
      onToggle={setQueryEnabled}
      placeholder={placeholder}
      hasValues={value.length > 0}
      renderContent={renderContent}
      onOpen={() => inputRef.current?.focus()}
      onClickInputArea={() => inputRef.current?.focus()}
      withoutRightIcon
    >
      {(opened) => (
        <SelectSearchInput
          sx={(theme) => ({ ...theme.typography.h3, width: value ? '100%' : undefined })}
          debounced={false}
          ref={inputRef}
          autoFocus={opened}
          value={value}
          onChangeText={onChange}
        />
      )}
    </DropdownSelect>
  );
};
